import { Fragment, type ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, XCircle } from 'lucide-react'
import type { IngestionRun, IngestionIssuesResponse } from '../../../../types/ingestion'
import {
  ARTEFACT_STATUS_COLOURS,
  HOSTNAME_MATCH_COLOURS,
  ISSUE_SEVERITY_COLOURS,
  canFlagCorrupt,
  canVerifyRun,
} from './run-detail-panel.helpers'

interface LabelProps {
  children: string
}

function FieldLabel({ children }: LabelProps): ReactElement {
  return <dt className="text-xs text-slate-500 dark:text-slate-400">{children}</dt>
}

function FieldValue({ children }: { children: ReactElement | string | number }): ReactElement {
  return <dd className="text-sm text-slate-700 dark:text-slate-300">{children}</dd>
}

function HostnameMatchIcon({ status }: { status: string }): ReactElement {
  if (status === 'matched')
    return <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
  if (status === 'mismatched') return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
  return <AlertCircle className="h-4 w-4 text-slate-400" />
}

interface ClientInfoGridProps {
  run: IngestionRun
  t: TFunction<'cases'>
}

export function ClientInfoGrid({ run, t }: ClientInfoGridProps): ReactElement {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
      <div className="flex flex-col">
        <FieldLabel>{t('detail.processing.hostname')}</FieldLabel>
        <dd className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {run.clientHostname ?? '—'}
        </dd>
      </div>
      <div className="flex flex-col">
        <FieldLabel>{t('detail.processing.os')}</FieldLabel>
        <FieldValue>{run.clientOs ?? '—'}</FieldValue>
      </div>
      <div className="flex flex-col">
        <FieldLabel>{t('detail.processing.fqdn')}</FieldLabel>
        <FieldValue>{run.clientFqdn ?? '—'}</FieldValue>
      </div>
      <div className="flex flex-col">
        <FieldLabel>{t('detail.processing.architecture')}</FieldLabel>
        <FieldValue>{run.clientArchitecture ?? '—'}</FieldValue>
      </div>
      <div className="flex flex-col">
        <FieldLabel>{t('detail.processing.timezone')}</FieldLabel>
        <FieldValue>{run.clientTimezone ?? '—'}</FieldValue>
      </div>
      <div className="flex flex-col">
        <FieldLabel>{t('detail.processing.hostnameMatch')}</FieldLabel>
        <dd
          className={`flex items-center gap-1 text-sm font-medium ${HOSTNAME_MATCH_COLOURS[run.hostnameMatchStatus] ?? ''}`}
        >
          <HostnameMatchIcon status={run.hostnameMatchStatus} />
          {t(`detail.processing.${run.hostnameMatchStatus}`)}
        </dd>
      </div>
    </dl>
  )
}

const ARTEFACT_SUMMARY_COLUMN_COUNT = 5

interface CollectionSettingsProps {
  parameters: Record<string, string | null> | null | undefined
  t: TFunction<'cases'>
}

function CollectionSettings({ parameters, t }: CollectionSettingsProps): ReactElement {
  const noteClass = 'text-xs text-slate-500 dark:text-slate-400'
  if (parameters === undefined || parameters === null)
    return <span className={noteClass}>{t('detail.processing.collectionSettingsNotRecorded')}</span>
  const pairs = Object.entries(parameters)
  if (pairs.length === 0)
    return <span className={noteClass}>{t('detail.processing.collectionSettingsNone')}</span>
  return (
    <details>
      <summary className={`cursor-pointer ${noteClass}`}>
        {t('detail.processing.collectionSettings')}
      </summary>
      <dl className="mt-1 space-y-0.5 font-mono text-xs text-slate-700 dark:text-slate-300">
        {pairs.map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <dt className="shrink-0">{key}</dt>
            <dd className="whitespace-pre-wrap">{value ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </details>
  )
}

interface ArtefactSummaryTableProps {
  run: IngestionRun
  t: TFunction<'cases'>
}

export function ArtefactSummaryTable({ run, t }: ArtefactSummaryTableProps): ReactElement {
  if (run.artefactSummary.length === 0) return <></>
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 dark:border-slate-600">
          <th className="py-1.5 pr-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.artefactType')}
          </th>
          <th className="py-1.5 pr-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.expectedRows')}
          </th>
          <th className="py-1.5 pr-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.parsedRows')}
          </th>
          <th className="py-1.5 pr-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.skippedRows')}
          </th>
          <th className="py-1.5 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.status')}
          </th>
        </tr>
      </thead>
      <tbody>
        {run.artefactSummary.map((entry) => (
          <Fragment key={entry.artefactType}>
            <tr className="border-b border-slate-100 dark:border-slate-700">
              <td className="py-1.5 pr-4 font-mono text-xs text-slate-900 dark:text-slate-100">
                {entry.artefactType}
              </td>
              <td className="py-1.5 pr-4 text-right text-slate-600 dark:text-slate-400">
                {entry.expectedRows?.toLocaleString() ?? '—'}
              </td>
              <td className="py-1.5 pr-4 text-right text-slate-600 dark:text-slate-400">
                {entry.parsedRows.toLocaleString()}
              </td>
              <td className="py-1.5 pr-4 text-right text-slate-600 dark:text-slate-400">
                {entry.skippedRows.toLocaleString()}
              </td>
              <td className="py-1.5">
                <span
                  className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${ARTEFACT_STATUS_COLOURS[entry.status] ?? ''}`}
                >
                  {entry.status}
                </span>
              </td>
            </tr>
            <tr
              data-testid={`collection-settings-${entry.artefactType}`}
              className="border-b border-slate-100 dark:border-slate-700"
            >
              <td colSpan={ARTEFACT_SUMMARY_COLUMN_COUNT} className="pb-1.5">
                <CollectionSettings parameters={entry.collectionParameters} t={t} />
              </td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}

interface ActionButtonsProps {
  run: IngestionRun
  onVerify: (runId: string) => void
  onFlagCorrupt: (runId: string) => void
  verifying: boolean
  flagging: boolean
  t: TFunction<'cases'>
}

export function ActionButtons({
  run,
  onVerify,
  onFlagCorrupt,
  verifying,
  flagging,
  t,
}: ActionButtonsProps): ReactElement {
  return (
    <div className="flex items-center gap-2">
      {canVerifyRun(run) && (
        <button
          onClick={() => {
            onVerify(run.id)
          }}
          disabled={verifying}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors disabled:opacity-50"
          style={{ background: 'var(--accent, #4e5e82)' }}
        >
          {verifying ? t('detail.processing.verifying') : t('detail.processing.verify')}
        </button>
      )}
      {canFlagCorrupt(run) && (
        <button
          onClick={() => {
            onFlagCorrupt(run.id)
          }}
          disabled={flagging}
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-transparent px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30"
        >
          {flagging ? t('detail.processing.flagging') : t('detail.processing.flagCorrupt')}
        </button>
      )}
    </div>
  )
}

interface IssuesTableProps {
  issues: IngestionIssuesResponse
  t: TFunction<'cases'>
}

function IssuesTable({ issues, t }: IssuesTableProps): ReactElement {
  return (
    <table className="mt-2 w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 dark:border-slate-600">
          <th className="py-1.5 pr-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.severity')}
          </th>
          <th className="py-1.5 pr-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.artefactType')}
          </th>
          <th className="py-1.5 pr-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.lineNumber')}
          </th>
          <th className="py-1.5 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('detail.processing.detail')}
          </th>
        </tr>
      </thead>
      <tbody>
        {issues.issues.map((issue) => (
          <tr key={issue.id} className="border-b border-slate-100 dark:border-slate-700">
            <td className="py-1.5 pr-3">
              <span
                className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${ISSUE_SEVERITY_COLOURS[issue.severity]}`}
              >
                {issue.severity}
              </span>
            </td>
            <td className="py-1.5 pr-3 font-mono text-xs text-slate-700 dark:text-slate-300">
              {issue.artefactType}
            </td>
            <td className="py-1.5 pr-3 text-right text-slate-500 dark:text-slate-400">
              {issue.lineNumber ?? '—'}
            </td>
            <td className="max-w-sm truncate py-1.5 text-slate-700 dark:text-slate-300">
              {issue.detail}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

interface IssuesSectionProps {
  issues: IngestionIssuesResponse | null
  issueCount: number
  expanded: boolean
  onToggle: () => void
  t: TFunction<'cases'>
}

export function IssuesSection({
  issues,
  issueCount,
  expanded,
  onToggle,
  t,
}: IssuesSectionProps): ReactElement {
  return (
    <div>
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between text-xs font-semibold tracking-wide text-slate-500 uppercase transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        aria-expanded={expanded}
      >
        <span>
          {t('detail.processing.issues')} ({issueCount})
        </span>
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {expanded && issueCount > 0 && issues && <IssuesTable issues={issues} t={t} />}
      {expanded && issueCount === 0 && (
        <p className="mt-2 py-3 text-sm text-slate-500 dark:text-slate-400">
          {t('detail.processing.noIssues')}
        </p>
      )}
    </div>
  )
}

interface PanelStatusProps {
  message: string
  variant: 'loading' | 'error'
}

export function PanelStatus({ message, variant }: PanelStatusProps): ReactElement {
  if (variant === 'loading') {
    return (
      <div
        className="py-6 text-center text-sm text-slate-500 dark:text-slate-400"
        role="status"
        aria-busy="true"
      >
        {message}
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 py-4 text-red-600" role="alert">
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span className="text-sm">{message}</span>
    </div>
  )
}
